<script>
import { cardReaderStatus } from "@/utils/cardReaderStatus";

export default {
  name: "CardReaderStatus",
  props: {
    floating: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    status() {
      return cardReaderStatus;
    },
    statusText() {
      const label = this.$t("cardreader.status.label");
      const state = this.$t(`cardreader.status.${this.status.state}`);
      const locale = (this.$i18n && this.$i18n.locale) || "";
      const tight = /^(zh|yue|ja)/.test(locale);
      return tight ? `${label}${state}` : `${label} · ${state}`;
    },
    tooltipText() {
      const key = `cardreader.status.tooltip.${this.status.state}`;
      const text = this.$t(key);
      if (this.status.state === "connected" && this.status.deviceName) {
        return `${text} (${this.status.deviceName})`;
      }
      return typeof text === "string" ? text : "";
    },
  },
};
</script>

<template>
  <router-link
    to="/clouds/nfcreader"
    class="card-reader-status"
    :class="['is-' + status.state, { 'is-float': floating }]"
    :title="tooltipText"
  >
    <span class="card-reader-status__dot" aria-hidden="true"></span>
    <i class="ri-bank-card-2-line" aria-hidden="true"></i>
    <span class="card-reader-status__text">{{ statusText }}</span>
  </router-link>
</template>

<style lang="scss" scoped>
.card-reader-status {
  display: inline-flex;
  align-items: center;
  max-width: 220px;
  height: 32px;
  margin: 0 8px;
  padding: 0 10px;
  border-radius: 18px;
  text-decoration: none;
  color: #495057;
  background: #f3f6f9;
  border: 1px solid #e6ebf1;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  white-space: nowrap;

  &:hover {
    text-decoration: none;
    color: #212529;
    background: #e9eef5;
  }

  i {
    font-size: 16px;
    margin-right: 6px;
  }
}

.card-reader-status__dot {
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.card-reader-status__text {
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-reader-status.is-connected {
  background: #eaf7f1;
  border-color: #c6ead7;
  color: #1b8a5a;

  .card-reader-status__dot {
    background: #34c38f;
    box-shadow: 0 0 0 3px rgba(52, 195, 143, 0.2);
  }

  .card-reader-status__label {
    color: #34c38f;
  }
}

.card-reader-status.is-ready {
  background: #eef2ff;
  border-color: #d4dcff;
  color: #3b4db8;

  .card-reader-status__dot {
    background: #556ee6;
  }

  .card-reader-status__label {
    color: #556ee6;
  }
}

.card-reader-status.is-connecting {
  background: #fff8e8;
  border-color: #f6e3b4;
  color: #b8860b;

  .card-reader-status__dot {
    background: #f1b44c;
    animation: card-reader-pulse 1.2s ease-in-out infinite;
  }

  .card-reader-status__label {
    color: #f1b44c;
  }
}

.card-reader-status.is-disconnected {
  .card-reader-status__dot {
    background: #adb5bd;
  }
}

.card-reader-status.is-error {
  background: #fdeeee;
  border-color: #f6cfcf;
  color: #c0392b;

  .card-reader-status__dot {
    background: #f46a6a;
  }

  .card-reader-status__label {
    color: #f46a6a;
  }
}

.card-reader-status.is-float {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 3000;
  margin: 0;
  box-shadow: 0 6px 18px rgba(15, 34, 58, 0.12);
}

@keyframes card-reader-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.45;
    transform: scale(0.75);
  }
}

@media (max-width: 767.98px) {
  .card-reader-status:not(.is-float) {
    max-width: 42px;
    padding: 0 10px;
    justify-content: center;

    i,
    .card-reader-status__text {
      display: none;
    }

    .card-reader-status__dot {
      margin-right: 0;
    }
  }
}
</style>
